import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Heart, 
  MapPin, 
  Music, 
  Calendar, 
  ShoppingBag,
  Gift,
  MessageCircle,
  Plus,
  Send,
  ChevronLeft
} from 'lucide-react';
import LoginModal from '@/components/LoginModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BandProfile = () => {
  const { bandId } = useParams();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const [band, setBand] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [comments, setComments] = useState({});
  const [hearts, setHearts] = useState({ band: false, tracks: {} });
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState({});
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginMessage, setLoginMessage] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  const handleProtectedAction = (action, message) => {
    if (isAuthenticated) {
      action();
    } else {
      setLoginMessage(message);
      setPendingAction(() => action);
      setLoginModalOpen(true);
    }
  };

  const handleLoginSuccess = () => {
    // Refresh the page to update auth state, then the action can be performed
    // The page will reload with user authenticated
    window.location.reload();
  };

  const fetchBandData = useCallback(async () => {
    try {
      // Fetch band
      const bandRes = await fetch(`${API}/bands/${bandId}`);
      if (!bandRes.ok) throw new Error('Band not found');
      const bandData = await bandRes.json();
      setBand(bandData);
      
      // Fetch tracks
      const tracksRes = await fetch(`${API}/bands/${bandId}/tracks`);
      if (tracksRes.ok) {
        const tracksData = await tracksRes.json();
        setTracks(tracksData);
        
        // Fetch comments for each track
        const commentsData = {};
        for (const track of tracksData) {
          const commentsRes = await fetch(`${API}/tracks/${track.track_id}/comments`);
          if (commentsRes.ok) {
            commentsData[track.track_id] = await commentsRes.json();
          }
        }
        setComments(commentsData);
      }
      
      // Check hearts if authenticated
      if (isAuthenticated) {
        const bandHeartRes = await fetch(`${API}/hearts/check/band/${bandId}`, {
          headers: getAuthHeaders(),
          
        });
        if (bandHeartRes.ok) {
          const { hearted } = await bandHeartRes.json();
          setHearts(prev => ({ ...prev, band: hearted }));
        }
        
        // Fetch user's playlists
        const playlistsRes = await fetch(`${API}/playlists/my`, {
          headers: getAuthHeaders(),
          
        });
        if (playlistsRes.ok) {
          setPlaylists(await playlistsRes.json());
        }
      }
    } catch (error) {
      console.error('Error fetching band:', error);
      toast.error('Failed to load band');
    } finally {
      setLoading(false);
    }
  }, [bandId, isAuthenticated, getAuthHeaders]);

  useEffect(() => {
    fetchBandData();
  }, [fetchBandData]);

  const toggleBandHeart = async () => {
    if (!isAuthenticated) {
      setLoginMessage('Sign in to heart bands and save your favorites.');
      setLoginModalOpen(true);
      return;
    }
    
    try {
      const res = await fetch(`${API}/hearts/band/${bandId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        const { hearted } = await res.json();
        setHearts(prev => ({ ...prev, band: hearted }));
        setBand(prev => ({
          ...prev,
          heart_count: prev.heart_count + (hearted ? 1 : -1)
        }));
        toast.success(hearted ? 'Band hearted!' : 'Heart removed');
      }
    } catch (error) {
      toast.error('Failed to update heart');
    }
  };

  const toggleTrackHeart = async (trackId) => {
    if (!isAuthenticated) {
      setLoginMessage('Sign in to heart tracks and build your collection.');
      setLoginModalOpen(true);
      return;
    }
    
    try {
      const res = await fetch(`${API}/hearts/track/${trackId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        const { hearted } = await res.json();
        setHearts(prev => ({
          ...prev,
          tracks: { ...prev.tracks, [trackId]: hearted }
        }));
        setTracks(prev => prev.map(t => 
          t.track_id === trackId 
            ? { ...t, heart_count: t.heart_count + (hearted ? 1 : -1) }
            : t
        ));
      }
    } catch (error) {
      toast.error('Failed to update heart');
    }
  };

  const addComment = async (trackId) => {
    if (!isAuthenticated) {
      setLoginMessage('Sign in to join the conversation and leave comments.');
      setLoginModalOpen(true);
      return;
    }
    
    const body = newComment[trackId]?.trim();
    if (!body) return;
    
    try {
      const res = await fetch(`${API}/tracks/${trackId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ body }),
        
      });
      
      if (res.ok) {
        const comment = await res.json();
        setComments(prev => ({
          ...prev,
          [trackId]: [comment, ...(prev[trackId] || [])]
        }));
        setNewComment(prev => ({ ...prev, [trackId]: '' }));
        toast.success('Comment added!');
      }
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const addToPlaylist = async (playlistId, trackId) => {
    try {
      const res = await fetch(`${API}/playlists/${playlistId}/tracks/${trackId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        toast.success('Track added to playlist!');
      }
    } catch (error) {
      toast.error('Failed to add to playlist');
    }
  };

  const getEmbedUrl = (url) => {
    // Convert various music URLs to embed format
    if (url.includes('spotify.com')) {
      if (!url.includes('/embed/')) {
        return url.replace('open.spotify.com/', 'open.spotify.com/embed/');
      }
      return url;
    }
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('soundcloud.com') && !url.includes('api.soundcloud.com')) {
      return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23CCFF00&auto_play=false`;
    }
    if (url.includes('bandcamp.com')) {
      // Bandcamp embeds are more complex, just return as-is for now
      return url;
    }
    return url;
  };

  const isYouTubeUrl = (url) => {
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  const getEmbedHeight = (track) => {
    const url = track.embed_url || '';
    // YouTube videos need at least 315px height (standard 560x315 aspect ratio)
    if (isYouTubeUrl(url)) {
      return 315;
    }
    // Spotify albums get larger height
    if (track.track_type === 'album') {
      return 352;
    }
    // Default for Spotify singles, SoundCloud, etc.
    return 152;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary font-accent text-3xl">LOADING...</div>
      </div>
    );
  }

  if (!band) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-accent text-4xl text-foreground mb-4">BAND NOT FOUND</h1>
          <Link to="/browse">
            <Button className="font-mono uppercase">Browse Bands</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="band-profile-page">
      {/* Login Modal */}
      <LoginModal 
        open={loginModalOpen} 
        onOpenChange={(open) => {
          setLoginModalOpen(open);
          if (!open) setPendingAction(null);
        }}
        message={loginMessage}
        onSuccess={handleLoginSuccess}
      />

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Link to="/browse" className="inline-flex items-center text-muted-foreground hover:text-primary font-mono text-sm no-underline">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Browse
        </Link>
      </div>

      {/* Hero Section */}
      <section className="border-b-2 border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Band Image */}
            <div className="relative">
              <div className="aspect-square border-2 border-border overflow-hidden bg-card">
                {band.image_urls?.[0] ? (
                  <img 
                    src={band.image_urls[0]}
                    alt={band.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    <span className="font-accent text-8xl text-muted-foreground">
                      {band.name[0]}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Band Info */}
            <div className="space-y-6">
              <div className="flex flex-wrap gap-2">
                {band.genre_tags?.map((tag, i) => (
                  <Badge 
                    key={i}
                    variant="outline"
                    className="font-mono text-xs uppercase px-2 py-1 border-muted-foreground text-muted-foreground"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
              
              <h1 className="font-accent text-5xl sm:text-6xl text-foreground leading-none">
                {band.name}
              </h1>
              
              {band.location && (
                <div className="flex items-center gap-2 text-muted-foreground font-mono text-sm">
                  <MapPin className="w-4 h-4" />
                  {band.location}
                </div>
              )}
              
              {band.email && (
                <div className="font-mono text-sm text-muted-foreground">
                  Contact: <a href={`mailto:${band.email}`} className="text-primary hover:underline">{band.email}</a>
                </div>
              )}
              
              <p 
                className="font-mono text-base text-foreground/80 leading-relaxed"
                dangerouslySetInnerHTML={{ 
                  __html: band.bio?.replace(/\n/g, '<br/>').replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '<br/><br/>') 
                }}
              />
              
              {/* Action Buttons */}
              <div className="flex items-center gap-4">
                <Button 
                  onClick={toggleBandHeart}
                  className={`border-2 font-mono uppercase tracking-widest btn-brutal ${
                    hearts.band 
                      ? 'border-secondary bg-secondary text-white' 
                      : 'border-secondary bg-transparent text-secondary hover:bg-secondary hover:text-white'
                  }`}
                  data-testid="heart-band-btn"
                >
                  <Heart className={`w-4 h-4 mr-2 ${hearts.band ? 'fill-current' : ''}`} />
                  {band.heart_count}
                </Button>
              </div>
              
              {/* External Links */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
                {band.website && (
                  <a href={band.website} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="font-mono text-xs uppercase">
                      <Music className="w-3 h-3 mr-2" />
                      Listen
                    </Button>
                  </a>
                )}
                {/* Multiple Merch Items */}
                {band.merch_items?.length > 0 && band.merch_items.map((item, idx) => (
                  <a key={idx} href={item.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="font-mono text-xs uppercase">
                      <ShoppingBag className="w-3 h-3 mr-2" />
                      {item.name || 'Merch'}
                    </Button>
                  </a>
                ))}
                {band.donate_link && (
                  <a href={band.donate_link} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="font-mono text-xs uppercase">
                      <Gift className="w-3 h-3 mr-2" />
                      Support
                    </Button>
                  </a>
                )}
              </div>
              
              {/* Upcoming Shows */}
              {band.upcoming_shows?.length > 0 && (
                <div className="pt-4 border-t border-border">
                  <h3 className="font-mono text-sm uppercase text-primary mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Upcoming Shows
                  </h3>
                  <div className="space-y-3">
                    {band.upcoming_shows.map((show, idx) => (
                      <div key={idx} className="bg-muted/30 border border-border p-3 rounded">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-sans font-bold text-foreground">{show.venue}</div>
                            <div className="font-mono text-xs text-muted-foreground flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {show.location}
                            </div>
                          </div>
                          {show.cost && (
                            <span className="font-mono text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                              {show.cost}
                            </span>
                          )}
                        </div>
                        <div className="font-mono text-sm text-foreground mt-2">
                          {show.date && (() => {
                            // Parse date as UTC to avoid timezone shifting
                            const [year, month, day] = show.date.split('-').map(Number);
                            const date = new Date(year, month - 1, day);
                            return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
                          })()}
                          {show.time && ` • ${show.time}`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Band Logo */}
              {band.logo_url && (
                <div className="pt-4 border-t border-border">
                  <img 
                    src={band.logo_url} 
                    alt={`${band.name} logo`}
                    className="h-16 object-contain"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Tracks Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-accent text-3xl text-foreground mb-8">MUSIC</h2>
          
          {tracks.length === 0 ? (
            <p className="font-mono text-muted-foreground">No tracks available yet.</p>
          ) : (
            <div className="space-y-8">
              {tracks.map((track) => (
                <div 
                  key={track.track_id} 
                  className="border-2 border-border bg-card p-6"
                  data-testid={`track-${track.track_id}`}
                >
                  {/* Track Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-sans text-xl font-bold text-foreground">
                        {track.title}
                      </h3>
                      <Badge variant="outline" className="font-mono text-[10px] uppercase mt-1">
                        {track.track_type}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleTrackHeart(track.track_id)}
                        className={hearts.tracks[track.track_id] ? 'text-secondary' : 'text-muted-foreground'}
                        data-testid={`heart-track-${track.track_id}`}
                      >
                        <Heart className={`w-4 h-4 mr-1 ${hearts.tracks[track.track_id] ? 'fill-current' : ''}`} />
                        {track.heart_count}
                      </Button>
                      
                      {isAuthenticated && playlists.length > 0 && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-muted-foreground hover:text-primary"
                              data-testid={`add-to-playlist-${track.track_id}`}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Playlist
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-card border-2 border-border">
                            <DialogHeader>
                              <DialogTitle className="font-sans">Add to Playlist</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3 pt-4">
                              {playlists.map((playlist) => (
                                <Button
                                  key={playlist.playlist_id}
                                  variant="outline"
                                  className="w-full justify-start font-mono text-sm"
                                  onClick={() => addToPlaylist(playlist.playlist_id, track.track_id)}
                                >
                                  {playlist.name}
                                </Button>
                              ))}
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                      
                      {!isAuthenticated && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-muted-foreground hover:text-primary"
                          onClick={() => handleProtectedAction(() => {}, 'Sign in to create playlists and save your favorite tracks.')}
                          data-testid={`add-to-playlist-login-${track.track_id}`}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Playlist
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Embed Player */}
                  <div className="mb-6 border border-border bg-background overflow-hidden" style={{ borderRadius: '12px' }}>
                    <iframe
                      src={getEmbedUrl(track.embed_url)}
                      width="100%"
                      height={getEmbedHeight(track)}
                      frameBorder="0"
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      className="border-0"
                      title={track.title}
                      allowFullScreen
                    />
                  </div>
                  
                  {/* Comments Section */}
                  <div className="border-t border-border pt-4">
                    <h4 className="font-mono text-sm uppercase text-muted-foreground mb-4 flex items-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      Comments ({comments[track.track_id]?.length || 0})
                    </h4>
                    
                    {/* Add Comment Form */}
                    <div className="flex gap-2 mb-4">
                      <Textarea
                        placeholder={isAuthenticated ? "Add a comment..." : "Sign in to comment..."}
                        value={newComment[track.track_id] || ''}
                        onChange={(e) => setNewComment(prev => ({ 
                          ...prev, 
                          [track.track_id]: e.target.value 
                        }))}
                        onClick={() => {
                          if (!isAuthenticated) {
                            setLoginMessage('Sign in to join the conversation and leave comments.');
                            setLoginModalOpen(true);
                          }
                        }}
                        className="bg-input border-border font-mono text-sm resize-none"
                        rows={2}
                        maxLength={500}
                        data-testid={`comment-input-${track.track_id}`}
                      />
                      <Button 
                        onClick={() => addComment(track.track_id)}
                        className="bg-primary text-black hover:bg-primary/80"
                        data-testid={`submit-comment-${track.track_id}`}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Comments List */}
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {comments[track.track_id]?.map((comment) => (
                        <div 
                          key={comment.comment_id}
                          className="border-l-2 border-muted pl-3 py-1"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-xs text-primary">
                              {comment.user_name}
                            </span>
                            <span className="font-mono text-[10px] text-muted-foreground">
                              {new Date(comment.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="font-mono text-sm text-foreground">
                            {comment.body}
                          </p>
                        </div>
                      ))}
                      
                      {(!comments[track.track_id] || comments[track.track_id].length === 0) && (
                        <p className="font-mono text-xs text-muted-foreground">
                          No comments yet. Be the first!
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BandProfile;
